import { BuilderConfig, FontAwesomeIconKey } from '../builderConfigs'
import * as BuilderContent from '../BuilderContentLayout'
import {
  ChangeEventHandler,
  useState,
  memo,
  useEffect,
  useMemo,
  CSSProperties,
  ReactNode,
} from 'react'
import PapaParse from 'papaparse'
import { useDispatch, useSelector } from '../../..'
import { setKeyForBuilder } from '../../../dux/actions/builders'
import {
  autoGenerateNumericRanges,
  CatalogTableData,
  FieldConfigData,
  FilterConfigData,
  groupLabelsBySection,
  LabeledNumericRange,
  RESERVED_FIELD_NAMES,
} from './catalogUtils'
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd'
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import { NumericFilterConfigEditor } from './NumericFilterConfigEditor'
import { openModal } from '../../../dux/actions/modal'
import { getEndpointById } from '../../../dux/modules/utils'
import { fromPairs, keyBy } from 'lodash'
import createId from '../../../utils/createId'

const MAX_TITLE_LENGTH = 100
const MAX_DESC_LENGTH = 500

const icon: FontAwesomeIconKey = 'fa-search'

export const CatalogBuilderConfig: BuilderConfig<'catalog'> = {
  key: 'catalog',
  builderHomeData: {
    type: 'catalog',
    description: (
      <p>
        Create a <i>Media Finder Asset</i>, combining tables of product data,
        images, and text.
      </p>
    ),
    enabled: false,
    icon: 'search',
    route: '/builders/catalog',
    title: 'Media Finder',
  },
  previewLabels: {
    title: 'Media Finder',
    description:
      'A Media Finder consists of tables of products which can be filtered based on ' +
      'product attributes. A Media Finder asset may be viewed on web or mobile.',
  },
  crumbTrail: {
    linkTo: '/builders/home',
    crumbText: 'Media Finder',
    crumbIcon: `fa ${icon}`,
  },
  fieldValidators: {
    title: {
      validateTrigger: 'onBlur',
      rules: [
        { required: true, message: 'Media Finder Name is required' },
        {
          max: MAX_TITLE_LENGTH,
          message: `Media Finder Name has a maximum character limit of ${MAX_TITLE_LENGTH}`,
        },
      ],
    },
    description: {
      validateTrigger: 'onBlur',
      rules: [
        {
          max: MAX_DESC_LENGTH,
          message: `Descriptive Copy has a maximum character limit of ${MAX_DESC_LENGTH}`,
        },
      ],
    },
  },
  formLabels: {
    name: 'Media Finder Name',
    description: 'Description',
    leadImage: 'Thumbnail',
  },
  icon,
  screenHandlers: {
    createScreen: {
      next: (saveToDb) => saveToDb(),
    },
    metaScreen: {
      next: (saveToDb) => saveToDb(),
    },
  },
  assetSerializer: (data) => {
    const { builderData, user } = data
    const assetMetaData = {
      id: builderData.id,
      asset_type: CatalogBuilderConfig.key,
      owner: user.id,
      title: builderData.title,
      description: builderData.description,
      tags: builderData.tags,
      color: '#333',
    }
    const assetData = {
      tables: builderData.tables,
      matrixFilterNames: builderData.matrixFilterNames,
      image: builderData.leadImage,
    }
    return {
      assetMetaData,
      assetData,
      ContentType: 'application/json',
      assetDataSerialized: JSON.stringify(assetData),
    }
  },
  Content: CatalogBuilderContent,
}

// this attempts to match Ant Design button styling in order to better style file input buttons
const BTN_STYLE: CSSProperties = {
  background: 'white',
  color: 'rgba(0, 0, 0, 0.88)',
  border: '1px solid #ccc',
  borderRadius: '4px',
  display: 'inline-block',
  width: 'fit-content',
  padding: '0 7px',
  cursor: 'pointer',
  boxShadow: '0 2px 0 rgba(255, 38, 5, 0.06)',
  height: '24px',
  fontSize: '14px',
  lineHeight: '1.5714285714285714',
}

/**
 * Given a CSV cell with possibly transformed content (untransformed CSV data
 * will either be a string or undefined), determine if the cell is empty.  An
 * empty cell is classified as any cell that is null, undefined, an empty string,
 * or a string containing only whitespace.
 */
function isCellEmpty(cell: null | undefined | number | boolean | string) {
  return (
    cell === null ||
    cell === undefined ||
    (typeof cell === 'string' && !/\S/.test(cell))
  )
}

/**
 * Transform cell data from CSV.  Primarily converts strings containing numbers (including numbers
 * starting with $) to numbers.
 */
function transformCellData(cell: string | number | undefined | null) {
  if (cell === undefined || cell === null) return undefined
  if (typeof cell === 'number') return cell
  // if the string contains any letters -- note that this prevents Number() from interpreting the
  // following as numbers:
  //  - 0x...  (hex numbers)
  //  - 0b...  (binary numbers)
  //  - 0o...  (octal numbers)
  //  - 0e...  (exponential notation)
  if (/[a-z]/i.test(cell)) return cell
  // strip any allowed currency prefixes to treat currencies as numbers
  const currencyPrefixStripped = cell.replace(/^\s*\$/, '')
  // if the Number() coercion doesn't work, it's not a number...note that we need this
  // because parseFloat will successfully parse a number followed by a non-number, which
  // is undesirable behavior in this case....
  if (!Number.isFinite(Number(currencyPrefixStripped))) return cell
  // replaces strings matching "$N" with "N" (i.e. replacing string dollar values with numbers)
  const n = parseFloat(currencyPrefixStripped)
  if (Number.isFinite(n)) return n
  return cell
}

/**
 * New and improved function to convert CSV to catalog data.  Can automatically handle header
 * grouping and data replacement (without resetting fields)!
 */
async function csvToCatalogTable(
  file: File,
  defaultName: string,
  replacingTable?: CatalogTableData,
): Promise<CatalogTableData> {
  return new Promise((res, reject) => {
    PapaParse.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: ({ data }) => {
        // name is in first row
        const name = data.shift()?.[0] || defaultName
        // column groups (if any) are in second row; a non-empty cell starts a new column group, and
        // a cell starting with an underscore ends any current column group
        const hRow2 = data.shift()
        // leaf column headers are in row 3
        const hRow3 = data.shift()
        if (!hRow2 || !hRow3) return reject(new Error('expected 3 header rows'))
        const headers = [
          ...hRow3
            .reduce<{
              headers: { index: number; value: string }[]
              currentGroup?: string
            }>(
              (a, h3, colIdx) => {
                const h2 = (hRow2[colIdx] || '').trim()
                if (h2 !== '') {
                  if (h2.startsWith('_')) a.currentGroup = undefined
                  else a.currentGroup = h2
                }
                a.headers[colIdx] = a.currentGroup
                  ? { index: colIdx, value: `${a.currentGroup} | ${h3}` }
                  : { index: colIdx, value: h3 || '' }
                return a
              },
              { headers: [] },
            )
            .headers.filter(
              // filter out any columns that don't have any data in them
              (h) => !data.every((row) => isCellEmpty(row[h.index])),
            ),
        ]
        // convert data to sparse maps and do data mapping
        const rows = data
          .map((row) =>
            fromPairs(
              headers
                .map((h) => {
                  const cell = transformCellData(row[h.index])
                  if (cell === '' || cell === undefined) return undefined
                  return [h.value, cell] as [string, string | number]
                })
                .filter((cell) => cell !== undefined),
            ),
          )
          .filter((row) => !Object.values(row).every(isCellEmpty))
        const previousFieldsByKey = replacingTable
          ? keyBy(replacingTable.fields, 'name')
          : undefined
        const fields = headers.map<FieldConfigData>((h) => {
          // if replacing an old table, attempt to match fields by key and use the old config if possible
          if (previousFieldsByKey?.[h.value])
            return previousFieldsByKey[h.value]
          const k = h.value
          const allValues = rows.map((row) => row[k])
          const filterConfig: FilterConfigData = allValues.every(
            (v) => typeof v === 'number',
          )
            ? { type: 'Numeric', ranges: autoGenerateNumericRanges(allValues) }
            : { type: 'Categorical' }
          const field: FieldConfigData = {
            name: k,
            filterConfig,
          }
          if (field.name === RESERVED_FIELD_NAMES.IMAGE)
            field.role = 'Image Asset ID'
          return field
        })
        // if there isn't an image asset field, insert it
        if (!fields.some((h) => h.role === 'Image Asset ID')) {
          fields.unshift({
            name: RESERVED_FIELD_NAMES.IMAGE,
            role: 'Image Asset ID',
            filterConfig: { type: 'Categorical' }, // config required even if not used
          })
        }
        res({
          id: replacingTable?.id || createId(),
          name,
          fields,
          rows,
          enabledFilterKeys: replacingTable?.enabledFilterKeys || [],
        } satisfies CatalogTableData)
      },
    })
  })
}

type CatalogBuilderContentProps = {
  endpoints: any
}
/**
 * Main content area for Catalog builder.  This includes all the controls for uploading CSVs, creating
 * tables, editing tables, etc.  In other words, other than the metadata panel (title, cover image etc.),
 * this edits all the relevant content of a catalog.
 */
function CatalogBuilderContent(props: CatalogBuilderContentProps) {
  const dispatch = useDispatch()

  // note: to actually get any of these properties to appear (like "tables"), the
  // property needs to be present in the initializer code in src/dux/reducers/builders.js
  const tables = useSelector(
    (state: any) => state.builders[CatalogBuilderConfig.key].tables || [],
  ) as CatalogTableData[]
  const setTables = (tables: CatalogTableData[]) =>
    dispatch(setKeyForBuilder(CatalogBuilderConfig.key, { tables }))
  const deleteTable = (deleteIndex: number) =>
    setTables(tables.filter((_t, i) => i !== deleteIndex))

  const matrixFilterNames = useSelector(
    (state: any) =>
      state.builders[CatalogBuilderConfig.key].matrixFilterNames || [],
  )
  const setMatrixFilterNames = (matrixFilterNames: string[]) =>
    dispatch(setKeyForBuilder(CatalogBuilderConfig.key, { matrixFilterNames }))

  const onFileChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.item(0)
    if (!file) return
    csvToCatalogTable(file, `Table ${tables.length + 1}`).then((table) =>
      setTables([...tables, table]),
    )
  }
  return (
    <BuilderContent.Layout>
      <BuilderContent.Main>
        {/* TODO: replace with react-dropzone or just something a little nicer */}
        <label style={BTN_STYLE}>
          Create new table from CSV
          <input
            type="file"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
        </label>
        <div>
          <h3>Matrix Filters</h3>
          <p>
            Matrix filters are global filters whose categories are column
            headers. For example, if a table has some "Compatible With" columns,
            they can be used with a "Product" matrix filter. The user selects a
            product, and only rows with a non-empty value in the associated
            product column.
          </p>
          <MatrixFilterEditor
            matrixFilterNames={matrixFilterNames}
            setMatrixFilterNames={setMatrixFilterNames}
          />
        </div>
        <CatalogTables
          tables={tables}
          setTables={setTables}
          deleteTable={deleteTable}
          endpoints={props.endpoints}
          matrixFilterNames={matrixFilterNames}
        />
      </BuilderContent.Main>
    </BuilderContent.Layout>
  )
}

type FieldConfigModalProps = {
  table: CatalogTableData
  updateTableData: (table: CatalogTableData) => void
  currentFieldKey: string | undefined
  matrixFilterNames: string[]
  onOk: VoidFunction
  onCancel: VoidFunction
}
/**
 * The modal for editing field configuration.  Currently, this only covers filter configuration,
 * but could/should be expanded to control other aspects of a given field.
 */
function FieldConfigModal(props: FieldConfigModalProps) {
  const {
    table,
    updateTableData,
    currentFieldKey,
    onOk,
    onCancel,
    matrixFilterNames,
  } = props
  const [ranges, setRanges] = useState<LabeledNumericRange[]>([])
  // filter order -1 indicates "not used" (i.e. disable this filter)
  const [filterOrder, setFilterOrder] = useState<number | string>(-1)
  const [filterType, setFilterType] = useState('Numeric')
  const [fieldIdx, field] = useMemo(() => {
    if (!currentFieldKey) {
      // reset controls
      return [undefined, undefined]
    }
    const idx = table.fields.findIndex((f) => f.name === currentFieldKey)
    if (idx === -1) {
      // shouldn't happen
      console.error(
        `currentFieldKey "${currentFieldKey}" not found in table ${table.name}`,
      )
      return [undefined, undefined]
    }
    const field = table.fields[idx]
    if (field.filterConfig.type === 'Matrix')
      setFilterOrder(field.filterConfig.filterName)
    else setFilterOrder(table.enabledFilterKeys.indexOf(field.name))
    setFilterType(field.filterConfig.type)
    setRanges('ranges' in field.filterConfig ? field.filterConfig.ranges : [])
    return [idx, field]
    // note that including table properties in the dependencies array breaks this!  hence the eslint disable
  }, [currentFieldKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!fieldIdx || !currentFieldKey) return
    const fields = [...table.fields]
    switch (filterType) {
      case 'Categorical':
        fields[fieldIdx] = {
          name: currentFieldKey,
          filterConfig: { type: 'Categorical' },
        }
        break
      case 'Numeric':
        fields[fieldIdx] = {
          name: currentFieldKey,
          filterConfig: { type: 'Numeric', ranges },
        }
        break
    }
    const enabledFilterKeys = table.enabledFilterKeys.filter(
      (k) => k !== currentFieldKey,
    )
    if (filterOrder !== -1 && typeof filterOrder === 'number')
      enabledFilterKeys.splice(filterOrder, 0, currentFieldKey)
    if (typeof filterOrder === 'string') {
      fields[fieldIdx] = {
        name: currentFieldKey,
        filterConfig: { type: 'Matrix', filterName: filterOrder },
      }
    }
    updateTableData({ ...table, fields, enabledFilterKeys })
    // note: including all the eslint-recommended deps will break this component, hence eslint disable below
  }, [ranges, filterOrder, filterType]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!field) return null
  const maxFilterCount = 3
  return (
    <Modal title={field.name} open onOk={onOk} onCancel={onCancel}>
      <Row gutter={18}>
        <Col span={12}>
          <Form.Item layout="vertical" label="Filter Assignment">
            <Select
              value={filterOrder}
              onChange={setFilterOrder}
              options={[
                { value: -1, label: 'Not Used' },
                ...Array.from({ length: maxFilterCount }).map((_, idx) => ({
                  value: idx,
                  label: `Filter ${idx + 1}`,
                })),
                ...matrixFilterNames.map((value) => ({
                  value,
                  label: `Matrix filter: ${value}`,
                })),
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          {filterOrder !== -1 && typeof filterOrder === 'number' && (
            <Form.Item layout="vertical" label="Filter Type">
              <Select
                value={filterType}
                onChange={setFilterType}
                options={[
                  { value: 'Numeric', label: 'Numeric Range' },
                  { value: 'Categorical', label: 'Categorical' },
                ]}
              />
            </Form.Item>
          )}
        </Col>
      </Row>
      {filterOrder !== -1 && filterType === 'Numeric' && (
        <NumericFilterConfigEditor
          ranges={ranges}
          setRanges={setRanges}
          values={table.rows.map((r) => r[field.name])}
        />
      )}
    </Modal>
  )
}

type MatrixFilterEditorProps = {
  matrixFilterNames: string[]
  setMatrixFilterNames: (matrixFilterNames: string[]) => void
}
/**
 * Stupid little control that allows editing of the matrix filter names.
 * Normally the matrix filters are shown in a comma separated list, with an "Edit"
 * button; when you press that, you get an edit box and "Ok" button.  When you press
 * "Ok", it splits on commas, trims all the names, and invokes the `setMatrixFilterNames`
 * props with that list.
 */
function MatrixFilterEditor(props: MatrixFilterEditorProps) {
  const { matrixFilterNames, setMatrixFilterNames } = props
  const [editingNames, setEditingNames] = useState<string | undefined>()
  return (
    <div>
      {editingNames === undefined ? (
        <>
          <span style={{ fontWeight: 'bold', marginRight: '28px' }}>
            {matrixFilterNames.join(', ')}
          </span>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditingNames(matrixFilterNames.join(', '))}
          >
            Edit
          </Button>
        </>
      ) : (
        <form
          onSubmit={() => {
            setMatrixFilterNames(editingNames.trim().split(/\s*,\s*/))
            setEditingNames(undefined)
          }}
        >
          <Space.Compact>
            <Input
              value={editingNames}
              onChange={(e) => setEditingNames(e.target.value)}
              autoFocus
            />
            <Button htmlType="submit" type="primary">
              Ok
            </Button>
          </Space.Compact>
        </form>
      )}
    </div>
  )
}

type FieldEditorProps = {
  value: string | undefined
  displayValue: ReactNode
  onUpdate: (value: string) => void
}
/**
 * Quick and dirty field editor; displays the contents of `displayValue` with an "Edit" button
 * next to it.  If the user presses the "Edit" button, `displayValue` is replaced with a
 * text edit box, and after the user presses "Ok", `onUpdate` is called and it goes back
 * to displaying `displayValue`.
 */
function FieldEditor(props: FieldEditorProps) {
  const { value, displayValue, onUpdate } = props
  const [editingValue, setEditingValue] = useState<string | undefined>()
  return editingValue === undefined ? (
    <>
      {displayValue}
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => setEditingValue(value || '')}
      >
        Edit
      </Button>
    </>
  ) : (
    <form
      onSubmit={() => {
        onUpdate(editingValue || '')
        setEditingValue(undefined)
      }}
    >
      <Space.Compact>
        <Input
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          autoFocus
        />
        <Button htmlType="submit" type="primary">
          Ok
        </Button>
      </Space.Compact>
    </form>
  )
}

type CatalogTableHeaderProps = {
  table: CatalogTableData
  onUpdate: (data: Partial<CatalogTableData>) => void
  deleteTable: VoidFunction
  onUpdateCsvData: ChangeEventHandler<HTMLInputElement>
}
/**
 * Header to display before every table in a catalog.  Contains editable table name,
 * a button to delete table (with confirmation pop-up), and a button to replace data
 * (without changing field configurations).
 *
 * TODO: when replacing data, there needs to be some feedback if the incoming CSV data
 * is incompatible with the existing table data.  For example, if a column has been removed,
 * any customizations to that field header will be lost on upload.  I recommend creating a custom
 * "UpdateCsvDataButton" component that contains this logic (but the `csvToCatalogTable` function
 * should return a list of warnings/errors to be presented in the UI).
 */
function CatalogTableHeader(props: CatalogTableHeaderProps) {
  const { table, onUpdate, deleteTable, onUpdateCsvData } = props
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5em',
        marginBottom: '1em',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '28px',
          alignItems: 'center',
          height: '46px',
        }}
      >
        <FieldEditor
          value={table.name}
          displayValue={<h3>{table.name}</h3>}
          onUpdate={(name) => onUpdate({ name })}
        />
        <label style={BTN_STYLE}>
          Update Data
          <input
            type="file"
            onChange={onUpdateCsvData}
            style={{ display: 'none' }}
          />
        </label>
        <Popconfirm
          title="Delete Table"
          description={
            <p>
              Are you sure you want to delete this table?
              <br />
              All customizations will be lost.
            </p>
          }
          onConfirm={deleteTable}
          okButtonProps={{ danger: true, icon: <DeleteOutlined /> }}
          okText="Delete"
        >
          <Button danger size="small" icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '0.5em',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '10em', textAlign: 'right', fontWeight: 'bold' }}>
          Description:
        </div>
        <FieldEditor
          value={table.description}
          displayValue={<span>{table.description}</span>}
          onUpdate={(description) => onUpdate({ description })}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '0.5em',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '10em', textAlign: 'right', fontWeight: 'bold' }}>
          Left header text:
        </div>
        <FieldEditor
          value={table.leftText}
          displayValue={<span>{table.leftText}</span>}
          onUpdate={(leftText) => onUpdate({ leftText })}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '0.5em',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '10em', textAlign: 'right', fontWeight: 'bold' }}>
          Right header text:
        </div>
        <FieldEditor
          value={table.rightText}
          displayValue={<span>{table.rightText}</span>}
          onUpdate={(rightText) => onUpdate({ rightText })}
        />
      </div>
    </div>
  )
}

type CatalogTablesProps = {
  deleteTable: (deleteIndex: number) => void
  tables: CatalogTableData[]
  setTables: (tables: CatalogTableData[]) => void
  endpoints: any
  matrixFilterNames: string[]
}
/**
 * Display catalog tables.  This function can be a performance beast, depending on the size
 * of the tables so it's memoized.
 */
const CatalogTables = memo(function CatalogTables(props: CatalogTablesProps) {
  const { tables, setTables, deleteTable, endpoints, matrixFilterNames } = props
  return tables.map((t, idx) => (
    <div key={idx}>
      <CatalogTable
        initialTable={t}
        setTable={(updatedTable) =>
          setTables(tables.map((t, i) => (i === idx ? updatedTable : t)))
        }
        deleteTable={() => deleteTable(idx)}
        endpoints={endpoints}
        matrixFilterNames={matrixFilterNames}
      />
    </div>
  ))
})

type FieldConfigHeaderProps = {
  table: CatalogTableData
  field: FieldConfigData
  onEditField: VoidFunction
}
/**
 * (Column) header for field configuration.  This includes a button to display the field
 * config editor modal, and an indicator of which filter position this column has, or if
 * it's configured as a matrix filter.
 */
function FieldConfigHeader(props: FieldConfigHeaderProps) {
  const { table, field, onEditField } = props
  const filterIdx = table.enabledFilterKeys.indexOf(field.name)
  const matrixFilter =
    field?.filterConfig.type === 'Matrix'
      ? field.filterConfig.filterName
      : undefined
  return (
    <th>
      <div
        style={{
          minWidth: '8ex',
          paddingLeft: '1ex',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {matrixFilter ? (
          <span>{matrixFilter && 'M'}</span>
        ) : (
          <span>{filterIdx === -1 ? undefined : filterIdx + 1}</span>
        )}
        <Tooltip title="Edit Field">
          <Button type="link" icon={<EditOutlined />} onClick={onEditField} />
        </Tooltip>
      </div>
    </th>
  )
}

type FieldHeaderProps = {
  label: string
}
/**
 * Field (column) header.  Will display system fields (e.g. `<<Image>>`) in
 * italics without brackets.
 */
function FieldHeader(props: FieldHeaderProps) {
  const { label } = props
  return /^<<.*>>$/.test(label) ? (
    <i>{label.replace(/^<<(.*)>>$/, '$1')}</i>
  ) : (
    label
  )
}

type CatalogTableProps = {
  initialTable: CatalogTableData
  setTable: (table: CatalogTableData) => void
  deleteTable: VoidFunction
  endpoints: any
  matrixFilterNames: string[]
}
/**
 * Displays the name and contents of a single catalog table, with controls to edit table name,
 * replace the data, delete the table, edit field configurations (filters, etc), and set item
 * (row) images.  Editing controls are mostly deferred to sub-components (e.g. `CatalogTableHeader`).
 */
function CatalogTable(props: CatalogTableProps) {
  const { initialTable, endpoints, matrixFilterNames, deleteTable } = props
  const [table, setTable] = useState(initialTable)
  // for performance reasons, we're buffering setTable; we'll call props.setTable on dialog close (OK)
  useEffect(() => setTable(initialTable), [initialTable])
  const fieldLabels = groupLabelsBySection(
    table.fields.filter((f) => typeof f.name === 'string').map((f) => f.name),
  )
  const [editFilterKey, setEditFilterKey] = useState<string | undefined>()
  const dispatch = useDispatch()
  const openSelectAssetModal = (rowIdx: number, fieldKey: string) => {
    const modalProps: any = {
      treeRoot: (getEndpointById('MST', endpoints) as any).rootFolders,
      filterType: 'published',
      saveCallback: (assetId: string, _folder: string, _displayText: string) =>
        props.setTable({
          ...table,
          rows: table.rows.map((r, i) =>
            i === rowIdx ? { ...table.rows[i], [fieldKey]: assetId } : r,
          ),
        }),
      modalTitle: 'Assets - Published',
      showTextOverride: true,
    }
    dispatch(openModal('SelectAssetModal', modalProps))
  }
  const onUpdateCsvData: ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.item(0)
    if (!file) return
    // note we have to use props.setTable here to write through to parent...we really need
    // a better naming convention to distinguish these two....
    csvToCatalogTable(file, table.name, table).then(props.setTable)
  }
  const fieldsByKey = useMemo(() => keyBy(table.fields, 'name'), [table.fields])
  return (
    <>
      <CatalogTableHeader
        deleteTable={deleteTable}
        table={table}
        onUpdate={(data) => props.setTable({ ...table, ...data })}
        onUpdateCsvData={onUpdateCsvData}
      />
      <FieldConfigModal
        table={table}
        updateTableData={setTable}
        currentFieldKey={editFilterKey}
        onOk={() => {
          props.setTable(table) // write table data through to parent
          setEditFilterKey(undefined)
        }}
        onCancel={() => setEditFilterKey(undefined)}
        matrixFilterNames={matrixFilterNames}
      />
      <table border={1}>
        <thead>
          {fieldLabels.some((l) => 'section' in l) && (
            <tr>
              {fieldLabels.map((l, idx) => (
                <th
                  key={idx}
                  colSpan={'section' in l ? l.items.length : undefined}
                >
                  {'section' in l ? l.section : ''}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {fieldLabels.flatMap((l) =>
              'section' in l ? (
                l.items.map((item) => <th key={item.index}>{item.label}</th>)
              ) : (
                <th key={l.index}>
                  <FieldHeader label={l.label} />
                </th>
              ),
            )}
          </tr>
          <tr>
            {fieldLabels.flatMap((l) => {
              return 'section' in l ? (
                l.items.map((item) => (
                  <FieldConfigHeader
                    key={item.index}
                    table={table}
                    field={fieldsByKey[item.key]}
                    onEditField={() => setEditFilterKey(item.key)}
                  />
                ))
              ) : (
                <FieldConfigHeader
                  key={l.index}
                  table={table}
                  field={fieldsByKey[l.key]}
                  onEditField={() => setEditFilterKey(l.key)}
                />
              )
            })}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {table.fields.map((f, colIdx) => (
                <td key={colIdx}>
                  {f.role === 'Image Asset ID' ? (
                    <div
                      style={{
                        minWidth: '8ex',
                        paddingLeft: '1ex',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ width: '20px' }}>
                        {row[f.name] && <CheckOutlined />}
                      </div>
                      <Button
                        type="link"
                        icon={<PictureOutlined />}
                        onClick={() => openSelectAssetModal(rowIdx, f.name)}
                      />
                    </div>
                  ) : (
                    (row[f.name] ?? '')
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
